import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { photoFrameModels, printSamples } from '@/mocks/frames'

export default function FrameShowcaseSection() {
  const { t } = useTranslation()

  return (
    <section id="frames" className="w-full bg-background-50 py-20 md:py-28">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12 md:mb-16">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-primary-500" />
              <span className="section-label text-primary-700 text-[11px] md:text-xs font-medium">
                {t('frames.label')}
              </span>
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold text-foreground-950 leading-tight">
              {t('frames.title')}
            </h2>
          </div>
          <p className="text-base text-foreground-600 leading-relaxed max-w-md">{t('frames.intro')}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {photoFrameModels.map((frame) => (
            <article
              key={frame.id}
              className="group rounded-lg overflow-hidden border border-background-200 bg-background-100/50"
            >
              <div className="relative w-full h-72 md:h-80 overflow-hidden">
                <img
                  src={frame.image}
                  alt={`${t(`frames.items.${frame.id}.name`)} — photo frame by Shabi Media`}
                  title={`${t(`frames.items.${frame.id}.name`)} | Shabi Media photo frames, ${frame.size}`}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute top-4 left-4 text-[11px] tracking-wider uppercase px-3 py-1 rounded-full bg-primary-500 text-background-50 whitespace-nowrap">
                  {t(`frames.items.${frame.id}.tag`)}
                </span>
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900">
                    {t(`frames.items.${frame.id}.name`)}
                  </h3>
                  <span className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 whitespace-nowrap">
                    {frame.size}
                  </span>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">{t(`frames.items.${frame.id}.desc`)}</p>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-16 md:mt-20">
          <div className="flex items-center gap-3 mb-8">
            <span className="h-px w-10 bg-primary-500" />
            <span className="section-label text-primary-700 text-[11px] md:text-xs font-medium">
              {t('frames.samplesLabel')}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {printSamples.map((sample) => (
              <article key={sample.id} className="rounded-lg overflow-hidden border border-background-200 bg-background-50">
                <div className="w-full h-44 md:h-48 overflow-hidden">
                  <img
                    src={sample.image}
                    alt={`${t(`frames.samples.${sample.id}.title`)} printing by Shabi Media`}
                    title={`${t(`frames.samples.${sample.id}.title`)} | Shabi Media print & design studio`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-5">
                  <h4 className="font-heading text-lg font-semibold text-foreground-900 mb-1">
                    <Link to="/contact" className="cursor-pointer">
                      {t(`frames.samples.${sample.id}.title`)}
                    </Link>
                  </h4>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {t(`frames.samples.${sample.id}.desc`)}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}